/* eslint-disable max-lines */
import { InlineMessage } from '@strata/react-inline-message';

import { ColorPalette } from 'components/brandGuidelines/ColorPalette';
import { ColorPaletteAccessible } from 'components/brandGuidelines/ColorPaletteAccessible';
import { ColorPaletteScale } from 'components/brandGuidelines/ColorPaletteScale';
import { ColorPaletteSingle } from 'components/brandGuidelines/ColorPaletteSingle';
import accessibleColorCombination from 'components/brandGuidelines/data/accessible-color-combinations.json';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const primaryColors = [
    <p key="1">
        When possible use only the primary color palette. Use only approved text and background
        color combinations.
    </p>,
    <ColorPaletteSingle
        key="2"
        colorPaletteSingleContent={
            <ColorPalette
                backgroundColor="#f36745"
                fontColor="#1d1f24"
                colorName="Orange"
                pmsValue="171C"
                hexValue="#f36745"
                cmykValue="0 74 77 0"
                rgbValue="243 103 69"
            />
        }
    />,
    <ColorPaletteSingle
        key="3"
        colorPaletteSingleContent={
            <ColorPalette
                backgroundColor="#003035"
                fontColor="#f2f3f7"
                colorName="Dark Green"
                pmsValue="7722C"
                hexValue="#003035"
                cmykValue="92 62 60 59"
                rgbValue="0 48 53"
            />
        }
    />,
    <ColorPaletteSingle
        key="4"
        colorPaletteSingleContent={
            <ColorPalette
                backgroundColor="#4fb287"
                fontColor="#1d1f24"
                colorName="Green"
                pmsValue="339C"
                hexValue="#4fb287"
                cmykValue="78 0 66 0"
                rgbValue="79 178 135"
            />
        }
    />,
    <ColorPaletteScale
        key="5"
        columnOneContent={
            <ColorPalette
                backgroundColor="#f2f3f7"
                fontColor="#1d1f24"
                colorName="Light Gray"
                pmsValue="656C"
                hexValue="#f2f3f7"
                cmykValue="4 2 1 0"
                rgbValue="242 243 247"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#e1e3e9"
                fontColor="#1d1f24"
                colorName="Medium Light Gray"
                pmsValue="656C"
                hexValue="#e1e3e9"
                cmykValue="10 7 4 0"
                rgbValue="225 227 233"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#adb2bf"
                fontColor="#1d1f24"
                colorName="Medium Gray"
                pmsValue="5295C"
                hexValue="#adb2bf"
                cmykValue="33 24 17 0"
                rgbValue="173 178 191"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#606574"
                fontColor="#f2f3f7"
                colorName="Medium Dark Gray"
                pmsValue="431C"
                hexValue="#606574"
                cmykValue="65 55 41 15"
                rgbValue="96 101 116"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#101114"
                fontColor="#f2f3f7"
                colorName="Dark Gray"
                pmsValue="419C"
                hexValue="#101114"
                cmykValue="75 69 64 81"
                rgbValue="16 17 20"
            />
        }
    />,
];

const secondaryColors = [
    <p key="6">
        You can use of the secondary color palette to categorize defined audiences and when needed
        for charts, icons, and other visual elements. Use only approved text and background color
        combinations.
    </p>,
    <ColorPaletteSingle
        key="7"
        colorPaletteSingleContent={
            <ColorPalette
                backgroundColor="#539bc9"
                fontColor="#1d1f24"
                colorName="Payer Blue"
                pmsValue="7688C"
                hexValue="#539bc9"
                cmykValue="66 27 7 0"
                rgbValue="83 155 201"
            />
        }
    />,
    <ColorPaletteSingle
        key="8"
        colorPaletteSingleContent={
            <ColorPalette
                backgroundColor="#fd226a"
                fontColor="#1d1f24"
                colorName="Pharmacy Pink"
                pmsValue="710C"
                hexValue="#fd226a"
                cmykValue="0 96 36 0"
                rgbValue="253 34 106"
            />
        }
    />,
    <ColorPaletteSingle
        key="9"
        colorPaletteSingleContent={
            <ColorPalette
                backgroundColor="#6361a4"
                fontColor="#f2f3f7"
                colorName="Patient Purple"
                pmsValue="7669C"
                hexValue="#6361a4"
                cmykValue="71 68 5 0"
                rgbValue="99 97 164"
            />
        }
    />,
    <ColorPaletteSingle
        key="10"
        colorPaletteSingleContent={
            <ColorPalette
                backgroundColor="#f52e1a"
                fontColor="#1d1f24"
                colorName="Provider Red"
                pmsValue="485C"
                hexValue="#f52e1a"
                cmykValue="0 95 99 0"
                rgbValue="244 46 25"
            />
        }
    />,
];

const accessibleColors = [
    <p key="11">
        Use the guide below to choose the appropriate combination of text and background color for
        marketing and communication purposes. These combinations are inclusive of the primary and
        secondary color palette only. Our applications use additional approved combinations
        generated from the expanded color palettes. Color contrast accessibility standards only
        apply to text. These standards do not apply to icons or illustrations.
    </p>,
    <InlineMessage
        key="12"
        wrapText
        intent={InlineMessage.Intent.TIP}
        title="Tip"
        label="Combinations below tagged AA Large require a minimum size text of 14px in bold weight"
    />,
    <div key="13" className="sds_color-palette-accessible__container">
        {accessibleColorCombination.map((color) => (
            <ColorPaletteAccessible
                key={color.description}
                fontColor={color.font_color}
                backgroundColor={color.background_color}
                description={color.description}
                compliance={color.compliance}
            />
        ))}
    </div>,
];

const expandedPrimaryColors = [
    <p key="14">
        The expanded primary color palette can used for illustrations and application design. Always
        ensure text passes AA color contrast requirements.
    </p>,
    <ColorPaletteScale
        key="15"
        columnOneContent={
            <ColorPalette
                backgroundColor="#fde6de"
                fontColor="#1d1f24"
                colorName="Orange #1"
                pmsValue="2015C"
                hexValue="#fde6de"
                cmykValue="0 11 9 0"
                rgbValue="253 230 222"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#f99b7a"
                fontColor="#1d1f24"
                colorName="Orange #2"
                pmsValue="1635C"
                hexValue="#f99b7a"
                cmykValue="0 47 50 0"
                rgbValue="247 156 123"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#f36745"
                fontColor="#1d1f24"
                colorName="Orange #3"
                pmsValue="171C"
                hexValue="#f36745"
                cmykValue="0 74 77 0"
                rgbValue="243 103 69"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#b73f23"
                fontColor="#f2f3f7"
                colorName="Orange #4"
                pmsValue="7226C"
                hexValue="#b73f23"
                cmykValue="16 92 100 6"
                rgbValue="197 57 39"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#831618"
                fontColor="#f2f3f7"
                colorName="Orange #5"
                pmsValue="1815C"
                hexValue="#831618"
                cmykValue="29 100 99 35"
                rgbValue="131 22 24"
            />
        }
    />,
    <ColorPaletteScale
        key="16"
        columnOneContent={
            <ColorPalette
                backgroundColor="#e3f2ed"
                fontColor="#1d1f24"
                colorName="Green #1"
                pmsValue="N/A"
                hexValue="#e3f2ed"
                cmykValue="10 0 7 0"
                rgbValue="227 243 237"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#a6d8c0"
                fontColor="#1d1f24"
                colorName="Green #2"
                pmsValue="7478C"
                hexValue="#a6d8c0"
                cmykValue="35 0 30 0"
                rgbValue="166 216 192"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#4fb287"
                fontColor="#1d1f24"
                colorName="Green #3"
                pmsValue="339C"
                hexValue="#4fb287"
                cmykValue="78 0 66 0"
                rgbValue="79 178 135"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#35866b"
                fontColor="#f2f3f7"
                colorName="Green #4"
                pmsValue="7724C"
                hexValue="#35866b"
                cmykValue="79 27 67 9"
                rgbValue="53 134 107"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#0b4240"
                fontColor="#f2f3f7"
                colorName="Green #5"
                pmsValue="3302C"
                hexValue="#0b4240"
                cmykValue="90 53 64 48"
                rgbValue="11 66 64"
            />
        }
    />,
    <ColorPaletteScale
        key="17"
        columnOneContent={
            <ColorPalette
                backgroundColor="#f2f3f7"
                fontColor="#1d1f24"
                colorName="Gray #1"
                pmsValue="656C"
                hexValue="#f2f3f7"
                cmykValue="4 2 1 0"
                rgbValue="242 243 247"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#e1e3e9"
                fontColor="#1d1f24"
                colorName="Gray #2"
                pmsValue="656C"
                hexValue="#e1e3e9"
                cmykValue="10 7 4 0"
                rgbValue="225 227 233"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#cacdd6"
                fontColor="#1d1f24"
                colorName="Gray #3"
                pmsValue="538C"
                hexValue="#cacdd6"
                cmykValue="20 15 10 0"
                rgbValue="202 205 214"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#adb2bf"
                fontColor="#1d1f24"
                colorName="Gray #4"
                pmsValue="5295C"
                hexValue="#adb2bf"
                cmykValue="33 24 17 0"
                rgbValue="173 178 191"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#606574"
                fontColor="#f2f3f7"
                colorName="Gray #5"
                pmsValue="431C"
                hexValue="#606574"
                cmykValue="65 55 41 15"
                rgbValue="96 101 116"
            />
        }
    />,
    <ColorPaletteScale
        key="18"
        columnOneContent={
            <ColorPalette
                backgroundColor="#4b4f5b"
                fontColor="#f2f3f7"
                colorName="Gray #6"
                pmsValue="7540C"
                hexValue="#4b4f5b"
                cmykValue="71 62 48 30"
                rgbValue="75 79 91"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#292c34"
                fontColor="#f2f3f7"
                colorName="Gray #7"
                pmsValue="426C"
                hexValue="#292c34"
                cmykValue="77 69 56 59"
                rgbValue="41 44 52"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#23252b"
                fontColor="#f2f3f7"
                colorName="Gray #8"
                pmsValue="426C"
                hexValue="#23252b"
                cmykValue="76 68 59 67"
                rgbValue="35 37 43"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#1d1f24"
                fontColor="#f2f3f7"
                colorName="Gray #9"
                pmsValue="419C"
                hexValue="#1d1f24"
                cmykValue="76 69 61 72"
                rgbValue="29 31 36"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#101114"
                fontColor="#f2f3f7"
                colorName="Gray #10"
                pmsValue="419C"
                hexValue="#101114"
                cmykValue="75 69 64 81"
                rgbValue="16 17 20"
            />
        }
    />,
];

const expandedSecondaryColors = [
    <p key="19">
        The expanded secondary color palette can be used for audience-specific illustrations. Always
        ensure text passes AA color contrast requirements.
    </p>,
    <ColorPaletteScale
        key="20"
        columnOneContent={
            <ColorPalette
                backgroundColor="#cfe8f7"
                fontColor="#1d1f24"
                colorName="Payer Blue #1"
                pmsValue="N/A"
                hexValue="#cfe8f7"
                cmykValue="17 2 0 0"
                rgbValue="207 232 247"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#93c6eb"
                fontColor="#1d1f24"
                colorName="Payer Blue #2"
                pmsValue="2905C"
                hexValue="#93c6eb"
                cmykValue="44 8 0 0"
                rgbValue="147 198 235"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#539bc9"
                fontColor="#1d1f24"
                colorName="Payer Blue #3"
                pmsValue="7688C"
                hexValue="#539bc9"
                cmykValue="66 27 7 0"
                rgbValue="83 155 201"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#316c9b"
                fontColor="#f2f3f7"
                colorName="Payer Blue #4"
                pmsValue="647C"
                hexValue="#316c9b"
                cmykValue="89 54 17 2"
                rgbValue="49 108 155"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#1d416d"
                fontColor="#f2f3f7"
                colorName="Payer Blue #5"
                pmsValue="7694C"
                hexValue="#1d416d"
                cmykValue="100 81 30 16"
                rgbValue="29 65 109"
            />
        }
    />,
    <ColorPaletteScale
        key="21"
        columnOneContent={
            <ColorPalette
                backgroundColor="#ffd6de"
                fontColor="#1d1f24"
                colorName="Pharmacy Pink #1"
                pmsValue="705C"
                hexValue="#ffd6de"
                cmykValue="0 20 3 0"
                rgbValue="255 214 222"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#ff9edf"
                fontColor="#1d1f24"
                colorName="Pharmacy Pink #2"
                pmsValue="189C"
                hexValue="#ff9edf"
                cmykValue="0 49 3 0"
                rgbValue="255 158 191"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#fd226a"
                fontColor="#1d1f24"
                colorName="Pharmacy Pink #3"
                pmsValue="710C"
                hexValue="#fd226a"
                cmykValue="0 96 36 0"
                rgbValue="253 34 106"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#e5004b"
                fontColor="#f2f3f7"
                colorName="Pharmacy Pink #4"
                pmsValue="1805C"
                hexValue="#e5004b"
                cmykValue="4 100 64 0"
                rgbValue="229 0 75"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#990033"
                fontColor="#f2f3f7"
                colorName="Pharmacy Pink #5"
                pmsValue="7421C"
                hexValue="#990033"
                cmykValue="26 100 74 23"
                rgbValue="153 0 51"
            />
        }
    />,
    <ColorPaletteScale
        key="22"
        columnOneContent={
            <ColorPalette
                backgroundColor="#e8e7fc"
                fontColor="#1d1f24"
                colorName="Patient Purple #1"
                pmsValue="N/A"
                hexValue="#e8e7fc"
                cmykValue="7 7 0 0"
                rgbValue="232 232 252"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#a6a3d4"
                fontColor="#1d1f24"
                colorName="Patient Purple #2"
                pmsValue="2705C"
                hexValue="#a6a3d4"
                cmykValue="34 33 0 0"
                rgbValue="166 163 212"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#6361a4"
                fontColor="#ffffff"
                colorName="Patient Purple #3"
                pmsValue="7669C"
                hexValue="#6361a4"
                cmykValue="71 68 5 0"
                rgbValue="99 97 164"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#4b4a7e"
                fontColor="#f2f3f7"
                colorName="Patient Purple #4"
                pmsValue="7671C"
                hexValue="#4b4a7e"
                cmykValue="82 79 24 8"
                rgbValue="75 74 126"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#2b2b4a"
                fontColor="#f2f3f7"
                colorName="Patient Purple #5"
                pmsValue="533C"
                hexValue="#2b2b4a"
                cmykValue="87 84 43 42"
                rgbValue="43 43 74"
            />
        }
    />,
    <ColorPaletteScale
        key="23"
        columnOneContent={
            <ColorPalette
                backgroundColor="#fcdedb"
                fontColor="#1d1f24"
                colorName="Provider Red #1"
                pmsValue="705C"
                hexValue="#fcdedb"
                cmykValue="0 15 8 0"
                rgbValue="253 222 219"
            />
        }
        columnTwoContent={
            <ColorPalette
                backgroundColor="#fbbbb4"
                fontColor="#1d1f24"
                colorName="Provider Red #2"
                pmsValue="169C"
                hexValue="#fbbbb4"
                cmykValue="0 32 21 0"
                rgbValue="251 187 180"
            />
        }
        columnThreeContent={
            <ColorPalette
                backgroundColor="#f52e1a"
                fontColor="#1d1f24"
                colorName="Provider Red #3"
                pmsValue="485C"
                hexValue="#f52e1a"
                cmykValue="0 95 99 0"
                rgbValue="244 46 25"
            />
        }
        columnFourContent={
            <ColorPalette
                backgroundColor="#9f1507"
                fontColor="#f2f3f7"
                colorName="Provider Red #4"
                pmsValue="187C"
                hexValue="#9f1507"
                cmykValue="24 99 99 21"
                rgbValue="159 21 7"
            />
        }
        columnFiveContent={
            <ColorPalette
                backgroundColor="#7f0c00"
                fontColor="#f2f3f7"
                colorName="Provider Red #5"
                pmsValue="1955C"
                hexValue="#7f0c00"
                cmykValue="29 99 99 38"
                rgbValue="127 12 0"
            />
        }
    />,
];

export const Colors = () => (
    <div>
        <Page
            pageTitle="Colors"
            pageDescription="Our primary, secondary, and expanded color palettes are a selection of colors that work together to create consistency across our marketing and products."
            pageContent={
                <>
                    <Section title="Primary Colors" contentArray={primaryColors} />
                    <Section title="Secondary Colors" contentArray={secondaryColors} />
                    <Section
                        title="Accessible Color Combinations"
                        contentArray={accessibleColors}
                    />
                    <Section title="Expanded Primary Colors" contentArray={expandedPrimaryColors} />
                    <Section
                        title="Expanded Secondary Colors"
                        contentArray={expandedSecondaryColors}
                    />
                </>
            }
        />
    </div>
);
